import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { Header } from "../../../../../components/Header/Header";
import AddSectionModal from "../AddSectionModal";
import DeleteModal from "../../../KbFolder/DeleteModal";
import ProposalLoadingPopup from "../ProposalLoadingPopup";
import api, { getFreshAccessToken } from "../../../../chat/api";
import { isPlanLimitError } from "../../../../../events/planLimitEvents";
import SourcesListModal from "../SourcesListModal";
import SourceViewModal from "../SourceViewModal";
import AddSubsectionModal from "../AddSubsectionModal";
import AssignMembersModal from "../Proposal Section View/AssignMembersModal";
import ProposalHeader from "./ProposalHeader";
import ProposalSection from "./ProposalSection";
import TeamMembersModal from "./TeamMembersModal";
import ProposalStepBar from "./Setup/ProposalStepBar";
import ProposalSetupStep from "./Setup/ProposalSetupStep";
import CreateProposalOnboardingModal from "./Setup/CreateProposalOnboardingModal";
import ProposalDraftStep from "./Draft/ProposalDraftStep";
import ProposalBuildingView, { ProposalBuildingPill } from "./Draft/ProposalBuildingView";
import ProposalNotReadyView from "./Draft/ProposalNotReadyView";
import ProposalReviewStep from "./Review/ProposalReviewStep";
import ProposalExportStep from "./Export/ProposalExportStep";
import GenerateSectionModal from "./Draft/GenerateSectionModal";
import RegenerateInstructionsModal from "./Draft/RegenerateInstructionsModal";
import ProposalCopilotPanel from "./Setup/ProposalCopilotPanel";
import { DEFAULT_TONE, DEFAULT_LENGTH, DEFAULT_COMPANION_DOCS, COMPANION_DOCS } from "./Setup/setupData";
import { listProposalSections } from "../../../../../services/proposalSectionsApi";
// Moved to services/ so Sensa's proposal flow waits on workers the same way.
import { waitForUploadStatus } from "../../../../../services/uploadStatus";

// Ordered wizard steps; gates forward navigation (advance only via "Continue").
const STEP_ORDER = ["setup", "draft", "review", "export"];

// Furthest reached step, persisted per project so unlocked tabs survive reloads.
const REACHED_STEP_PREFIX = "proposal_reached_step_";

// First-time onboarding popup on the Setup step — seen state is app-level, not per-project.
const ONBOARDING_SEEN_KEY = "proposal_onboarding_seen";

const readReachedStep = (projectId) => {
  if (!projectId) return 0;
  try {
    const raw = localStorage.getItem(`${REACHED_STEP_PREFIX}${projectId}`);
    const n = raw != null ? parseInt(raw, 10) : 0;
    return Number.isInteger(n)
      ? Math.min(Math.max(n, 0), STEP_ORDER.length - 1)
      : 0;
  } catch (e) {
    return 0;
  }
};

// Resolve a section/subsection reference (uuid OR number like "1.2") to the
// node's id — the key generatingTargets/the editor overlay are indexed by.
const findNodeId = (toc, target) => {
  const t = String(target);
  for (const s of toc || []) {
    if (!s || typeof s !== "object") continue;
    if (String(s.id) === t || String(s.number) === t) return s.id;
    for (const c of s.children || []) {
      if (c && (String(c.id) === t || String(c.number) === t)) return c.id;
    }
  }
  return target;
};

// Find a node's saved body in the analysis TOC by target id/number (mirrors
// the backend's node matching in regenerate). Used to snapshot the previous
// version before a rewrite so the completion toast can offer Undo.
const findNodeResponse = (toc, targetId) => {
  const t = String(targetId);
  for (const s of toc || []) {
    if (!s || typeof s !== "object") continue;
    if (String(s.id) === t || String(s.number) === t) return s.response || "";
    for (const c of s.children || []) {
      if (c && (String(c.id) === t || String(c.number) === t))
        return c.response || "";
    }
  }
  return "";
};

// Patch a node's response/sources in place (immutably) by id/number, straight
// from a "completed" frame's metadata — lets the editor show the final draft
// the instant the stream ends, instead of waiting on the follow-up refetch.
const applyNodeResponse = (proposalData, targetId, body, sources) => {
  if (!proposalData?.analysis?.toc) return proposalData;
  const t = String(targetId);
  const patch = (node) => ({
    ...node,
    response: body,
    ...(sources ? { sources } : {}),
  });
  let changed = false;
  const toc = proposalData.analysis.toc.map((s) => {
    if (!s || typeof s !== "object") return s;
    if (String(s.id) === t || String(s.number) === t) {
      changed = true;
      return patch(s);
    }
    if (!s.children?.length) return s;
    let childChanged = false;
    const children = s.children.map((c) => {
      if (c && (String(c.id) === t || String(c.number) === t)) {
        childChanged = true;
        return patch(c);
      }
      return c;
    });
    if (!childChanged) return s;
    changed = true;
    return { ...s, children };
  });
  if (!changed) return proposalData;
  return { ...proposalData, analysis: { ...proposalData.analysis, toc } };
};

const adaptSectionViewData = (apiData) => {
  if (!apiData) return apiData;
  if (apiData.analysis && apiData.analysis.toc) return apiData;
  if (apiData.sections) {
    return {
      ...apiData,
      analysis: {
        toc: apiData.sections.filter(Boolean).map((section) => ({
          ...section,
          sources: section.source || section.sources || [],
          children: (section.subsections || []).filter(Boolean).map((sub) => ({
            ...sub,
            sources: sub.source || sub.sources || [],
          })),
        })),
      },
    };
  }
  return apiData;
};

// Wait for a worker to finish over the /ws/upload-status/ socket. Opens a
// dedicated socket with a fresh token, filters frames to `statusKey`, and
// settles exactly once. A quiet close/error/timeout resolves `true` — the task
// usually finished and the frame was missed; the follow-up fetch decides.
// Returns { promise, cancel } so callers can abandon the wait on unmount.

// Backend supports only formal | professional | technical; other Setup chips disabled.
const TONE_API_TO_LABEL = {
  formal: "Formal & Compliance",
  professional: "Professional & Results",
  technical: "Innovative & Visionary",
};

const TONE_LABEL_TO_API = Object.fromEntries(
  Object.entries(TONE_API_TO_LABEL).map(([key, label]) => [label, key])
);

// Coerce usps/avoid (strings or objects) into { id, value }; id null until saved.
const toConfigItems = (list) =>
  (list || []).map((it) =>
    typeof it === "string"
      ? { id: null, value: it }
      : { id: it?.id ?? null, value: it?.value ?? "" }
  );


// Step-bar "Regenerate" button, shown once the active node has content.
//
// This was a dropdown offering "Change Knowledge Scope" / "Insert Tables" /
// "Add Custom Instructions" — three routes to the same regenerate call, which
// forced the user to pick a mechanism before saying what they wanted. Scope and
// instructions now live together in one dialog, so this is a single action.
//
// Currently unrendered: regenerate moved to the per-node hover toolbar. Kept for
// the commented-out step-bar actions further down.
// eslint-disable-next-line no-unused-vars
const RegenerateMenu = ({ isSubsection, disabled, onRegenerate }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onRegenerate}
    className="px-3 py-1 rounded-md border border-[#E5E7EB] text-[#374151] font-outfit text-[14px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
  >
    {isSubsection ? "Regenerate Subsection" : "Regenerate Section"}
  </button>
);

const ProposalPage = () => {
  const { projectId, step } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Registered by the Export step so its action renders in the step-bar row.
  const [exportControls, setExportControls] = useState(null);

  const orgId = useMemo(() => {
    const pathParts = location.pathname.split("/");
    const orgIdIndex = pathParts.indexOf("org") + 1;
    return pathParts[orgIdIndex] || null;
  }, [location.pathname]);

  const [isAddSubsectionModalOpen, setIsAddSubsectionModalOpen] = useState(false);
  const [currentSectionForSub, setCurrentSectionForSub] = useState(null);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [proposalData, setProposalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [projectName, setProjectName] = useState("");
  // RFP submission deadline (ISO date) from the section-view API; shown in the header.
  const [deadline, setDeadline] = useState(null);
  const [selectedItems, setSelectedItems] = useState({
    sectionIds: [],
    subsectionIds: [],
  });
  const [hoveredSection, setHoveredSection] = useState(null);
  const [hoveredSubsectionId, setHoveredSubsectionId] = useState(null);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({
    type: null,
    sectionId: null,
    subsectionId: null,
  });
  const [folderId, setFolderId] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [generateSectionModalOpen, setGenerateSectionModalOpen] = useState(false);
  // When the KB-scope modal opens via Regenerate → "Change Knowledge Scope",
  // its result regenerates the node rather than running a fresh generation.
  const [customInstructionOpen, setCustomInstructionOpen] = useState(false);
  const [draftActiveNode, setDraftActiveNode] = useState(null);
  // Live frames from the first-run agentic analysis; non-null while it runs.
  const [analysisProgress, setAnalysisProgress] = useState(null);
  // Set when the backend refuses to build (nothing ingested yet, etc.) — shown
  // in place of the build view, since the fix is the user's to make.
  const [analysisBlocked, setAnalysisBlocked] = useState(null);
  const [doneOverrides, setDoneOverrides] = useState({});
  const [markDoneStatus, setMarkDoneStatus] = useState("idle"); // idle|loading|success
  // Targets currently (re)generating, keyed by targetId → { percent, message }.
  const [generatingTargets, setGeneratingTargets] = useState({});
  const draftGenWsListRef = useRef([]);

  const [isSourcesListOpen, setIsSourcesListOpen] = useState(false);
  const [currentSources, setCurrentSources] = useState([]);
  const [isSourceViewOpen, setIsSourceViewOpen] = useState(false);
  const [selectedSourceData, setSelectedSourceData] = useState(null);

  const [isTeamMembersModalOpen, setIsTeamMembersModalOpen] = useState(false);
  const [rightViewTab, setRightViewTab] = useState("comments");
  const [filterType, setFilterType] = useState("All");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedMemberIds, setExpandedMemberIds] = useState(new Set());
  const [activeRightSection, setActiveRightSection] = useState(null);

  const [expandedContentSubsections, setExpandedContentSubsections] = useState({});

  const [editingSubsectionId, setEditingSubsectionId] = useState(null);
  const [editingSectionContentId, setEditingSectionContentId] = useState(null);
  const [generatingSubsectionId, setGeneratingSubsectionId] = useState(null);
  const [generatingSectionId, setGeneratingSectionId] = useState(null);

  const [collapsedSectionContents, setCollapsedSectionContents] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // Active step lives in the URL so it survives reloads and is shareable.
  const currentStep = STEP_ORDER.includes(step) ? step : "setup";

  // First-time "how this wizard works" popup — shown once on Setup, the step
  // every "Create Proposal" click lands on. Dismissing it either way marks it
  // seen for good; it never depends on `projectId` since it's app-level, not
  // per-project.
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      return localStorage.getItem(ONBOARDING_SEEN_KEY) !== "true";
    } catch (e) {
      return false;
    }
  });
  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    try {
      localStorage.setItem(ONBOARDING_SEEN_KEY, "true");
    } catch (e) {
      /* localStorage unavailable — popup will reappear next visit, harmless */
    }
  }, []);

  // Base path up to ".../proposal" (keeps the optional /org/:orgId segment).
  const proposalBasePath = useMemo(() => {
    const marker = "/proposal";
    const i = location.pathname.indexOf(marker);
    return i === -1 ? location.pathname : location.pathname.slice(0, i + marker.length);
  }, [location.pathname]);

  // Project Files lives OUTSIDE the proposal wizard — it's a sibling tab of
  // ".../proposal", not a step within it. Derived by trimming the marker so the
  // optional /org/:orgId segment is preserved.
  const projectFilesPath = useMemo(
    () => `${proposalBasePath.replace(/\/proposal$/, "")}/project-files`,
    [proposalBasePath]
  );

  // Furthest unlocked step; clicking beyond it is ignored. Seeded from the higher
  // of the URL's step and the persisted value so reached tabs survive reloads.
  const [reachedStepIndex, setReachedStepIndex] = useState(() =>
    Math.max(
      0,
      STEP_ORDER.indexOf(STEP_ORDER.includes(step) ? step : "setup"),
      readReachedStep(projectId)
    )
  );

  useEffect(() => {
    if (!projectId) return;
    try {
      localStorage.setItem(
        `${REACHED_STEP_PREFIX}${projectId}`,
        String(reachedStepIndex)
      );
    } catch (e) {
      /* ignore storage errors */
    }
  }, [projectId, reachedStepIndex]);
  const [draftLoading, setDraftLoading] = useState(false);

  // Pre-arm the Draft loader synchronously so the sample fallback never flashes.
  const armDraftLoadingFor = useCallback(
    (stepKey) => {
      if (stepKey === "draft" && !proposalData?.analysis?.toc?.length) {
        setDraftLoading(true);
      }
    },
    [proposalData]
  );

  const goToStep = useCallback(
    (stepKey) => {
      armDraftLoadingFor(stepKey);
      navigate(`${proposalBasePath}/${stepKey}`);
    },
    [proposalBasePath, navigate, armDraftLoadingFor]
  );

  // Sync the reached index when the URL deep-links to a further step.
  useEffect(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    setReachedStepIndex((r) => Math.max(r, idx));
  }, [currentStep]);

  const handleContinueStep = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    const nextIdx = Math.min(idx + 1, STEP_ORDER.length - 1);
    setReachedStepIndex((r) => Math.max(r, nextIdx));
    goToStep(STEP_ORDER[nextIdx]);
  }, [currentStep, goToStep]);

  const handleStepClick = useCallback(
    (stepKey) => {
      const idx = STEP_ORDER.indexOf(stepKey);
      if (idx <= reachedStepIndex) goToStep(stepKey);
    },
    [reachedStepIndex, goToStep]
  );

  // Only read by the commented-out step-bar "Mark as Done" button — the hover
  // toolbar derives done-state per node instead. Kept for the uncomment.
  // eslint-disable-next-line no-unused-vars
  const activeNodeDone = draftActiveNode
    ? (doneOverrides[draftActiveNode.id] ?? draftActiveNode.markAsDone) === true
    : false;

  // `explicitNode` lets the in-document hover toolbar mark a node done without
  // first waiting for the selection state to commit — the step-bar button still
  // calls this with no argument and acts on the active node.
  const handleMarkActiveDone = useCallback(async (explicitNode) => {
    const node = explicitNode?.targetId ? explicitNode : draftActiveNode;
    if (!node?.targetId || markDoneStatus === "loading") return;
    setMarkDoneStatus("loading");
    try {
      await api.post(
        `api/v1/rfp/folders/${folderId || projectId}/section-view/mark-as-done/`,
        { target_id: node.targetId, done: true }
      );
      setDoneOverrides((prev) => ({ ...prev, [node.id]: true }));
      setMarkDoneStatus("success");
      setTimeout(() => setMarkDoneStatus("idle"), 1500);
    } catch (error) {
      console.error("Mark as done failed:", error);
      toast.error("Couldn't mark as done. Please try again.");
      setMarkDoneStatus("idle");
    }
  }, [draftActiveNode, markDoneStatus, folderId, projectId]);

  const [selectedTone, setSelectedTone] = useState(DEFAULT_TONE);
  // Sent as-is: the API value and the UI value are the same string, unlike
  // tone which carries a display label the payload has to map back.
  const [selectedLength, setSelectedLength] = useState(DEFAULT_LENGTH);
  const [usps, setUsps] = useState([]);
  const [customInstructions, setCustomInstructions] = useState("");
  const [selectedCompanionDocs, setSelectedCompanionDocs] = useState(
    DEFAULT_COMPANION_DOCS
  );
  const [customDocs, setCustomDocs] = useState([]);

  // Proposal Structure (section 04). `structure_source` is 'agentic'
  // (Auto-Extract from RFP, the default) or 'template'; when 'template' a
  // `template_id` from the folder's org must be linked. `selectedTemplateName` is
  // echoed by the config GET so the link can render the chosen template without a
  // second fetch. The old single-prompt 'ai' source is no longer selectable — it
  // survives only as the backend's internal fallback if the agentic team fails.
  const [structureSource, setStructureSource] = useState("agentic");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);
  const templatesFetchedRef = useRef(false);

  // Auto-save state. pendingSaveRef is set only by user edits, so the initial
  // hydration from the loaded config never triggers a save.
  const [saveStatus, setSaveStatus] = useState("idle"); // idle|saving|saved|error
  const pendingSaveRef = useRef(false);
  const saveTimerRef = useRef(null);
  // Debounce window (ms); Custom Instructions uses a longer wait, clicks save fast.
  const saveDelayRef = useRef(800);
  // Folder already fetched for, so the mount effect doesn't fire twice (StrictMode).
  const configFetchedForRef = useRef(null);
  const markConfigDirty = useCallback((delay = 800) => {
    pendingSaveRef.current = true;
    saveDelayRef.current = delay;
  }, []);

  const toggleCompanionDoc = useCallback(
    (id) => {
      markConfigDirty();
      setSelectedCompanionDocs((prev) =>
        prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
      );
    },
    [markConfigDirty]
  );

  // Wrapped setters that mark a user edit so the debounced auto-save fires.
  const handleToneChange = useCallback(
    (value) => {
      markConfigDirty();
      setSelectedTone(value);
    },
    [markConfigDirty]
  );
  const handleLengthChange = useCallback(
    (value) => {
      markConfigDirty();
      setSelectedLength(value);
    },
    [markConfigDirty]
  );
  const handleUspsChange = useCallback(
    (value) => {
      markConfigDirty();
      setUsps(value);
    },
    [markConfigDirty]
  );
  const handleCustomInstructionsChange = useCallback(
    (value) => {
      markConfigDirty(2000); // wait 2s after the user stops typing
      setCustomInstructions(value);
    },
    [markConfigDirty]
  );

  const [rightTab, setRightTab] = useState("brief");
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  // Copilot chat state lives here so it survives the panel closing/reopening.
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotChatId, setCopilotChatId] = useState(null);

  // Review opens the Copilot panel by default (Copilot tab) on entry, mirroring
  // Setup — but here the close button stays available so the user can collapse
  // it. Keyed on the step, so a manual close isn't re-opened until they return.
  useEffect(() => {
    if (currentStep === "review") {
      setRightTab("copilot");
      setRightPanelOpen(true);
    }
  }, [currentStep]);

  const wsRef = useRef(null);
  const generationStartedRef = useRef(true);
  const showActionButtonsRef = useRef(true);
  const generatingSubsectionRef = useRef(null);

  const wsCompleteCallbackRef = useRef(null);
  const wsErrorCallbackRef = useRef(null);

  const [editTitle, setEditTitle] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editSectionTitle, setEditSectionTitle] = useState("");

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [activeAssignSection, setActiveAssignSection] = useState({
    idx: null, title: "", sectionId: null, assignedMembers: []
  });

  const isReviewer = proposalData?.user_permission === "reviewer";

  useEffect(() => {
    if (proposalData?.analysis?.toc?.length > 0 && !activeRightSection) {
      setActiveRightSection(proposalData.analysis.toc[0]);
    }
  }, [proposalData, activeRightSection]);

  const toggleMemberCard = (memberId) => {
    setExpandedMemberIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) newSet.delete(memberId);
      else newSet.add(memberId);
      return newSet;
    });
  };

  const getFilteredMembers = () => {
    if (!activeRightSection?.assigned_members) return [];
    if (filterType === "All") return activeRightSection.assigned_members;
    return activeRightSection.assigned_members.filter((member) => {
      const role = member.role || "";
      return role.toLowerCase() === filterType.toLowerCase();
    });
  };

  const getInitials = (fullName) => {
    if (!fullName) return "U";
    const parts = fullName.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Maps a saved proposal_config object onto the Setup step UI.
  const applyProposalConfig = useCallback((proposalConfig) => {
    if (!proposalConfig) return;
    setSelectedTone(TONE_API_TO_LABEL[proposalConfig.tone] || DEFAULT_TONE);
    setSelectedLength(proposalConfig.length || DEFAULT_LENGTH);
    setUsps(toConfigItems(proposalConfig.usps));
    setCustomInstructions(proposalConfig.custom_instructions || "");
    // Proposal Structure. `template_name` is null when no template is linked, or
    // when the linked template was later deleted — the Setup UI flags the latter.
    // Configs saved before 'ai' was retired come back normalized to 'agentic'.
    setStructureSource(proposalConfig.structure_source || "agentic");
    setSelectedTemplateId(proposalConfig.template_id || null);
    setSelectedTemplateName(proposalConfig.template_name || null);
    // Companion-doc checkboxes are hydrated from `proposal_documents` (below),
    // not here — the config object doesn't echo their booleans back.
  }, []);

  useEffect(() => {
    const {
      projectName: name,
      folderId: locFolderId,
    } = location.state || {};

    const effectiveFolderId = locFolderId || projectId;
    setFolderId(effectiveFolderId);
    if (name) setProjectName(name);

    // Fetch the latest config once per folder (guard avoids a duplicate call).
    if (configFetchedForRef.current !== effectiveFolderId) {
      configFetchedForRef.current = effectiveFolderId;

      const fetchProposalConfig = async () => {
        setLoading(true);
        try {
          const freshToken = localStorage.getItem("token");
          const response = await api.get(
            `api/v1/rfp/folders/${effectiveFolderId}/proposal-config/`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${freshToken}`,
              },
            }
          );
          if (response.status === 200 || response.status === 201) {
            applyProposalConfig(response.data.proposal_config);
            const proposalDocs = response.data.proposal_documents || [];
            // Default companion docs: match enabled defaults by name to COMPANION_DOCS ids.
            setSelectedCompanionDocs(
              proposalDocs
                .filter((d) => d.is_default && d.enabled)
                .map((d) => COMPANION_DOCS.find((c) => c.title === d.name)?.id)
                .filter(Boolean)
            );
            setCustomDocs(proposalDocs.filter((d) => !d.is_default));
          }
        } catch (error) {
          console.error("Error fetching proposal config:", error);
          toast.error("Failed to load proposal configuration");
        } finally {
          setLoading(false);
        }
      };
      fetchProposalConfig();
    }

    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [location.state, projectId, navigate, applyProposalConfig]);

  // usps/avoid sent as plain value strings; backend appends and assigns ids.
  const buildProposalConfigPayload = useCallback(
    () => ({
      tone: TONE_LABEL_TO_API[selectedTone] || "professional",
      length: selectedLength,
      custom_instructions: customInstructions,
      usps: usps.map((u) => u.value),
      // Guardrails removed from the UI; always send an empty list.
      avoid: [],
      cover_letter: selectedCompanionDocs.includes("cover_letter"),
      submission_checklist: selectedCompanionDocs.includes("submission_checklist"),
    }),
    [selectedTone, selectedLength, customInstructions, usps, selectedCompanionDocs]
  );

  // Backfill backend ids onto local items missing one (matched by value) so they
  // can later be removed via /remove/. Raw setters so this never re-triggers a save.
  const syncConfigItemIds = useCallback((proposalConfig) => {
    if (!proposalConfig) return;
    const idByValue = (list) => {
      const map = {};
      (list || []).forEach((it) => {
        if (it && it.value != null) map[String(it.value).toLowerCase()] = it.id;
      });
      return map;
    };
    const uspIds = idByValue(proposalConfig.usps);
    setUsps((prev) =>
      prev.map((it) =>
        it.id ? it : { ...it, id: uspIds[it.value.toLowerCase()] ?? null }
      )
    );
  }, []);

  // Monotonic counter so out-of-order concurrent refetches can't overwrite newer
  // data with older — only the latest request's result is applied.
  // Moved above saveProposalConfig/handleCreateCustomDoc/handleToggleCustomDoc
  // (originally declared much later in this component) because a useCallback's
  // dependency array is evaluated immediately, in source order, on every
  // render — unlike a plain reference inside a callback BODY, which only runs
  // later. Those three all list this in their own deps, so declaring it any
  // later throws "Cannot access before initialization" on first render.
  const fetchSeqRef = useRef(0);
  const fetchAnalysisData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    try {
      const response = await api.get(
        `api/v1/rfp/folders/${folderId}/section-view/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // A newer refetch was started after this one — drop this (stale) result.
      if (seq !== fetchSeqRef.current) return;

      if (response.status === 200 || response.status === 201) {
        setProposalData(adaptSectionViewData(response.data));
        if (response.data.folder_name) {
          setProjectName(response.data.folder_name);
        }
        setDeadline(response.data.deadline || null);
      }
    } catch (error) {
      console.error("Error refreshing analysis data:", error);
    }
  }, [folderId, token]);

  // POST the updated config silently — only the save-status indicator updates.
  const saveProposalConfig = useCallback(
    async (payload) => {
      if (!folderId) return;
      try {
        const freshToken = localStorage.getItem("token");
        const response = await api.post(
          `api/v1/rfp/folders/${folderId}/proposal-config/`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${freshToken}`,
            },
          }
        );
        if (response.status === 200 || response.status === 201) {
          syncConfigItemIds(response.data?.proposal_config);
          setSaveStatus("saved");
          // A cover_letter/submission_checklist toggle in this payload injects
          // or removes a TOC section server-side (_sync_proposal_docs_into_toc)
          // — without this, Draft/Review kept rendering the pre-toggle TOC
          // from the shared proposalData snapshot until an unrelated action
          // forced a refetch. Harmless no-op for a plain tone/instructions
          // save that touched no TOC section.
          if (response.data?.toc_updated) await fetchAnalysisData();
        } else {
          setSaveStatus("error");
        }
      } catch (error) {
        console.error("Failed to save proposal config:", error);
        setSaveStatus("error");
      }
    },
    [folderId, syncConfigItemIds, fetchAnalysisData]
  );

  // Optimistically remove a USP (raw setter → no auto-save); revert on
  // failure. Local-only items (no id) just drop without an API call.
  const removeConfigItem = useCallback(
    async (item, setter) => {
      setter((prev) => prev.filter((it) => it !== item));
      if (!item?.id || !folderId) return;
      setSaveStatus("saving");
      try {
        const freshToken = localStorage.getItem("token");
        await api.post(
          `api/v1/rfp/folders/${folderId}/proposal-config/remove/`,
          { id: item.id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${freshToken}`,
            },
          }
        );
        setSaveStatus("saved");
      } catch (error) {
        console.error("Failed to remove proposal config item:", error);
        toast.error("Failed to remove item");
        setSaveStatus("error");
        setter((prev) => [...prev, item]);
      }
    },
    [folderId]
  );

  const handleRemoveUsp = useCallback(
    (item) => removeConfigItem(item, setUsps),
    [removeConfigItem]
  );

  // Returns the promise so the modal can show Saving/Saved (throws on error).
  const handleCreateCustomDoc = useCallback(
    async ({ name, description }) => {
      if (!folderId) return;
      const freshToken = localStorage.getItem("token");
      const response = await api.post(
        `api/v1/rfp/folders/${folderId}/proposal-config/`,
        { custom_docs: [{ name, description }] },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
          },
        }
      );
      // Refresh from the authoritative response (new doc has an id now).
      setCustomDocs(response.data?.proposal_config?.custom_docs || []);
      // The new doc is injected as a TOC section server-side
      // (_sync_proposal_docs_into_toc) — without this, Draft/Review kept
      // showing the pre-add TOC from the shared proposalData snapshot until
      // some unrelated action forced a refetch.
      if (response.data?.toc_updated) await fetchAnalysisData();
    },
    [folderId, fetchAnalysisData]
  );

  // Toggle a custom doc — optimistic local update + persist by id.
  const handleToggleCustomDoc = useCallback(
    async (id) => {
      const doc = customDocs.find((d) => d.id === id);
      if (!doc) return;
      const nextEnabled = !doc.enabled;
      setCustomDocs((prev) =>
        prev.map((d) => (d.id === id ? { ...d, enabled: nextEnabled } : d))
      );
      if (!folderId) return;
      // Same top-of-page Saving/Saved indicator the default-doc toggles
      // already show (via the debounced saveProposalConfig path) — this one
      // was silent, the only visible sign of a save was the checkbox itself.
      setSaveStatus("saving");
      try {
        const freshToken = localStorage.getItem("token");
        const response = await api.post(
          `api/v1/rfp/folders/${folderId}/proposal-config/`,
          { custom_docs: [{ id, enabled: nextEnabled }] },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${freshToken}`,
            },
          }
        );
        setSaveStatus("saved");
        // Enabling/disabling adds or removes this doc's TOC section
        // server-side — same refetch reason as handleCreateCustomDoc above.
        if (response.data?.toc_updated) await fetchAnalysisData();
      } catch (error) {
        console.error("Failed to toggle custom document:", error);
        toast.error("Failed to update document");
        setSaveStatus("error");
        setCustomDocs((prev) =>
          prev.map((d) => (d.id === id ? { ...d, enabled: doc.enabled } : d))
        );
      }
    },
    [customDocs, folderId, fetchAnalysisData]
  );

  // Load the org's proposal section templates for the structure picker. Org is
  // resolved from the selected_org_id cookie inside proposalSectionsApi.
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(false);
    try {
      const results = await listProposalSections();
      setTemplates(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error("Failed to load proposal section templates:", error);
      setTemplatesError(true);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Fetch templates once the folder is known (guarded so it runs a single time).
  useEffect(() => {
    if (!folderId || templatesFetchedRef.current) return;
    templatesFetchedRef.current = true;
    loadTemplates();
  }, [folderId, loadTemplates]);

  // Persist the proposal structure. Optimistic with rollback; POSTs only the
  // structure fields (the config endpoint is partial, so tone/usps/docs are
  // untouched). For 'agentic' the linked template is cleared to satisfy the
  // backend (a template_id is dropped unless structure_source is 'template').
  const saveStructureSource = useCallback(
    async (source, templateId = null, templateName = null) => {
      if (!folderId) return;
      const prev = {
        source: structureSource,
        id: selectedTemplateId,
        name: selectedTemplateName,
      };
      // Optimistic update.
      setStructureSource(source);
      setSelectedTemplateId(source === "template" ? templateId : null);
      setSelectedTemplateName(source === "template" ? templateName : null);
      setSaveStatus("saving");
      try {
        const freshToken = localStorage.getItem("token");
        const payload =
          source === "template"
            ? { structure_source: "template", template_id: templateId }
            : { structure_source: "agentic" };
        const response = await api.post(
          `api/v1/rfp/folders/${folderId}/proposal-config/`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${freshToken}`,
            },
          }
        );
        if (response.status === 200 || response.status === 201) {
          setSaveStatus("saved");
        } else {
          throw new Error(`Server returned ${response.status}`);
        }
      } catch (error) {
        console.error("Failed to save proposal structure:", error);
        toast.error(
          error.response?.data?.error ||
            "Couldn't update proposal structure. Please try again."
        );
        // Roll back to the pre-save selection.
        setStructureSource(prev.source);
        setSelectedTemplateId(prev.id);
        setSelectedTemplateName(prev.name);
        setSaveStatus("error");
      }
    },
    [folderId, structureSource, selectedTemplateId, selectedTemplateName]
  );

  // Auto-Extract: commit immediately (no-op if already 'ai').
  const handleSelectAiStructure = useCallback(() => {
    if (structureSource === "ai") return;
    saveStructureSource("ai");
  }, [structureSource, saveStructureSource]);

  // "Use a Template" radio: only reflect the choice in the UI. Nothing is saved
  // until a template is actually picked — a template source without a template_id
  // is rejected by the backend.
  const handleChooseTemplateSource = useCallback(() => {
    setStructureSource("template");
  }, []);

  // A template was picked in the dropdown → commit it.
  const handlePickTemplate = useCallback(
    (tpl) => {
      if (!tpl?.id) return;
      saveStructureSource("template", tpl.id, tpl.name);
    },
    [saveStructureSource]
  );

  const handleViewAllTemplates = useCallback(() => {
    navigate("/account/proposal-sections");
  }, [navigate]);

  // The linked template exists in config but couldn't be resolved (deleted, or
  // no longer visible) — GET returns a null name in that case.
  const templateUnavailable =
    structureSource === "template" &&
    !!selectedTemplateId &&
    !selectedTemplateName;

  // Debounced auto-save; only fires after a real user edit (pendingSaveRef), so
  // hydration from the loaded config is skipped.
  useEffect(() => {
    if (!pendingSaveRef.current) return;

    const payload = buildProposalConfigPayload();
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      pendingSaveRef.current = false;
      // Show "Saving…" only once the debounce settles, not on every keystroke.
      setSaveStatus("saving");
      saveProposalConfig(payload);
    }, saveDelayRef.current);

    return () => clearTimeout(saveTimerRef.current);
  }, [
    selectedTone,
    usps,
    customInstructions,
    selectedCompanionDocs,
    buildProposalConfigPayload,
    saveProposalConfig,
  ]);

  useEffect(() => {
    const hasSelections =
      selectedItems.sectionIds.length > 0 ||
      selectedItems.subsectionIds.length > 0;
    if (hasSelections) {
      showActionButtonsRef.current = true;
    }
  }, [selectedItems]);

  // Auto-dismiss the "saved" indicator after 3s.
  useEffect(() => {
    if (saveStatus !== "saved") return;
    const timer = setTimeout(() => setSaveStatus("idle"), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  // The RFP deadline is folder-level, so load it once on mount for every step —
  // the section list itself is lazily loaded only on Draft/Review/Export. Reads
  // just the deadline (never touches proposalData) and stays silent if the
  // analysis doesn't exist yet (section-view 404s until `analyze/` has run).
  useEffect(() => {
    if (!folderId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(
          `api/v1/rfp/folders/${folderId}/section-view/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled && (res.status === 200 || res.status === 201)) {
          setDeadline(res.data?.deadline || null);
        }
      } catch (_) {
        /* no analysis yet (404) or transient error — leave deadline null */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folderId, token]);

  // Generate the analysis/TOC if needed. `section-view/` 404s until `analyze/`
  // completes, so Draft must ensure analysis first. The backend is now ASYNC:
  //   - cache hit  → 200 with the analysis (nothing to wait for)
  //   - cache miss → 202 {task_id, status_key}; the agentic analysis runs in a
  //     worker (~1-4 min) and streams progress on the upload-status socket.
  // This resolves only once the analysis exists (or failed), so callers can
  // fetch section-view right after. Progress is surfaced via a loading toast.
  const ensureAnalysis = useCallback(async () => {
    const orgId = Cookies.get("selected_org_id");
    const url = orgId
      ? `api/v1/rfp/folders/${folderId}/analyze/?organization_id=${encodeURIComponent(orgId)}`
      : `api/v1/rfp/folders/${folderId}/analyze/`;
    try {
      const res = await api.post(
        url,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Cached analysis → ready now.
      if (res.status !== 202) return true;

      // 202 accepted → wait for the worker over the upload-status socket.
      // Analyze (shred + architect + review) can take a few minutes, so the
      // progress drives a full build view rather than a one-line toast.
      // Seed from the worker's last frame when re-attaching to a run already in
      // flight (leaving the page and coming back). Hard-resetting to 5% here made
      // a run that was genuinely at 45% look like it had started over, and the
      // next real frame can be a minute away.
      const last = res.data?.last_status;
      setAnalysisProgress({
        percent: typeof last?.progress === "number" ? last.progress : 5,
        message: last?.message || "Starting RFP analysis…",
      });
      const wait = waitForUploadStatus({
        statusKey: res.data?.status_key,
        timeoutMs: 10 * 60 * 1000,
        onProgress: (data) =>
          setAnalysisProgress({
            percent: typeof data.progress === "number" ? data.progress : undefined,
            message: data.message || "",
          }),
        // A failed build is a state, not a notification. A toast disappears and
        // leaves the user staring at a progress view that never moves — show the
        // failure in place, with the reason and a way to act on it.
        onError: (data) =>
          setAnalysisBlocked({
            reason: "build_failed",
            detail:
              data?.message ||
              "The proposal build failed. Check your documents and try again.",
          }),
      });
      analysisWaitCancelRef.current = wait.cancel;
      const ok = await wait.promise;
      analysisWaitCancelRef.current = null;
      setAnalysisProgress(null);
      return ok;
    } catch (error) {
      // 409 = the folder isn't ready to build (nothing uploaded, still
      // ingesting, wrong document type, ingestion failed). Not an error to
      // swallow: it's the actionable state, so show it instead of a build.
      if (error?.response?.status === 409) {
        setAnalysisProgress(null);
        setAnalysisBlocked({
          reason: error.response.data?.reason || "not_ready",
          detail:
            error.response.data?.error ||
            "Upload and ingest an RFP document before building a proposal.",
        });
        return false;
      }
      console.error("Error generating analysis (TOC):", error);
      setAnalysisProgress(null);
      return false;
    }
  }, [folderId, token]);

  // Abandon an in-flight analysis wait on unmount so the socket and its
  // safety timer don't outlive the page.
  const analysisWaitCancelRef = useRef(null);
  useEffect(() => () => analysisWaitCancelRef.current?.(), []);

  // Trailing refetch after generations settle, for a final guaranteed-fresh read.
  const genRefetchTimerRef = useRef(null);
  const scheduleGenRefetch = useCallback(() => {
    if (genRefetchTimerRef.current) clearTimeout(genRefetchTimerRef.current);
    genRefetchTimerRef.current = setTimeout(() => {
      genRefetchTimerRef.current = null;
      fetchAnalysisData();
    }, 700);
  }, [fetchAnalysisData]);

  // Generations are serialized: the backend rewrites the whole TOC on each save,
  // so two at once would let one overwrite the other. Queued nodes still spin.
  const genQueueRef = useRef([]);
  const genActiveRef = useRef(false);

  const clearGeneratingTarget = useCallback((targetId) => {
    setGeneratingTargets((prev) => {
      if (!(targetId in prev)) return prev;
      const next = { ...prev };
      delete next[targetId];
      return next;
    });
  }, []);

  // Run one generation to completion; resolves when the upload-status socket
  // reports completed/error (or drops), so the queue can advance.
  const runGeneration = useCallback(
    (job) =>
      new Promise((resolve) => {
        const {
          targetId,
          isSubsection,
          kb_folder_ids,
          kb_file_keys,
          rfp_folder_ids,
          rfp_file_keys,
          kb_whole,
        } = job;
        const fid = folderId || projectId;
        const body = {
          ...(isSubsection
            ? { subsection_id: targetId }
            : { section_id: targetId }),
          kb_folder_ids,
          kb_file_keys,
          rfp_folder_ids,
          rfp_file_keys,
          kb_whole,
        };
        const orgId = Cookies.get("selected_org_id");
        const url = orgId
          ? `api/v1/rfp/folders/${fid}/generate-subsections/?organization_id=${encodeURIComponent(
              orgId
            )}`
          : `api/v1/rfp/folders/${fid}/generate-subsections/`;

        setGeneratingTargets((prev) => ({
          ...prev,
          [targetId]: { percent: 0, message: "Starting generation…" },
        }));

        (async () => {
          let settled = false;
          let ws = null;
          let timer = null;
          // Single funnel for every exit path. ALWAYS clears the spinner so a
          // quiet socket close or the safety timeout can't wedge it on forever
          // (previously only the explicit completed/error/onerror frames cleared
          // it — a graceful onclose left the target spinning indefinitely while
          // the already-generated content sat behind the overlay). When content
          // may exist, refetch so it maps even if the "completed" frame was
          // dropped — a socket close is far more reliable than a specific frame.
          const finish = ({ refetch = false } = {}) => {
            if (settled) return;
            settled = true;
            if (timer) clearTimeout(timer);
            if (ws) {
              try {
                ws.close();
              } catch (e) {
                /* noop */
              }
              draftGenWsListRef.current = draftGenWsListRef.current.filter(
                (w) => w !== ws
              );
            }
            clearGeneratingTarget(targetId);
            if (refetch) fetchAnalysisData();
            resolve();
          };
          try {
            const res = await api.post(url, body, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (![200, 201, 202].includes(res.status)) {
              throw new Error(`Server returned ${res.status}`);
            }
            const statusKey = res.data?.status_key;
            const freshToken = await getFreshAccessToken();
            ws = new WebSocket(
              `${process.env.REACT_APP_WS_URL || "ws://localhost:8000"}/ws/upload-status/?token=${freshToken}`
            );
            draftGenWsListRef.current.push(ws);
            // Safety net so a silent socket never wedges the queue or the
            // spinner; refetch in case the content finished but no frame arrived.
            timer = setTimeout(() => finish({ refetch: true }), 5 * 60 * 1000);
            ws.onmessage = (event) => {
              try {
                const data = JSON.parse(event.data);
                if (statusKey && data.pdf_key && data.pdf_key !== statusKey)
                  return;
                if (data.type === "progress") {
                  setGeneratingTargets((prev) =>
                    prev[targetId]
                      ? {
                          ...prev,
                          [targetId]: {
                            ...prev[targetId],
                            percent:
                              typeof data.progress === "number"
                                ? data.progress
                                : 0,
                            message: data.message || "Generating content…",
                          },
                        }
                      : prev
                  );
                  return;
                }
                // Live drafting text — accumulate deltas so the editor shows
                // the response as it's written instead of a silent spinner.
                if (data.type === "stream") {
                  const delta = data.metadata?.delta || "";
                  if (!delta) return;
                  setGeneratingTargets((prev) =>
                    prev[targetId]
                      ? {
                          ...prev,
                          [targetId]: {
                            ...prev[targetId],
                            message: data.message || "Drafting the response…",
                            streamText:
                              (prev[targetId].streamText || "") + delta,
                          },
                        }
                      : prev
                  );
                  return;
                }
                if (data.type === "completed") {
                  // Paint the final body/bodies straight from the frame so the
                  // editor doesn't sit empty/stale while the reconciliation
                  // refetch (below) is still in flight. Single-node generation
                  // carries one body on `metadata.body`; generating a whole
                  // section with empty subsections ("family mode" on the
                  // backend) drafts several nodes at once and carries one
                  // entry per node on `metadata.updates` instead.
                  const finalBody = data.metadata?.body;
                  const updates = Array.isArray(data.metadata?.updates)
                    ? data.metadata.updates
                    : [];
                  if (typeof finalBody === "string" || updates.length) {
                    setProposalData((prev) => {
                      let next = prev;
                      if (typeof finalBody === "string") {
                        next = applyNodeResponse(
                          next,
                          targetId,
                          finalBody,
                          data.metadata?.sources
                        );
                      }
                      for (const u of updates) {
                        if (typeof u?.body === "string" && u?.target_id != null) {
                          next = applyNodeResponse(
                            next,
                            u.target_id,
                            u.body,
                            u.sources
                          );
                        }
                      }
                      return next;
                    });
                  }
                  toast.success(
                    job.title ? `"${job.title}" drafted.` : "Content generated."
                  );
                  finish({ refetch: true });
                } else if (data.type === "error" || data.error) {
                  toast.error(data.message || "Generation failed.");
                  finish();
                }
              } catch (e) {
                /* ignore malformed frames */
              }
            };
            // A quiet close (proxy idle-timeout, backend closing early, a missed
            // "completed" frame) fires onclose without onerror — refetch so the
            // already-saved content maps and the spinner is cleared regardless.
            ws.onerror = () => finish({ refetch: true });
            ws.onclose = () => finish({ refetch: true });
          } catch (error) {
            console.error("Generate section failed:", error);
            if (!isPlanLimitError(error)) {
              toast.error(
                error.response?.data?.error || "Failed to start generation."
              );
            }
            // The request never started, so there's nothing new to fetch.
            finish();
          }
        })();
      }),
    [folderId, projectId, token, fetchAnalysisData, clearGeneratingTarget]
  );

  const processGenQueue = useCallback(async () => {
    if (genActiveRef.current) return;
    genActiveRef.current = true;
    while (genQueueRef.current.length > 0) {
      const job = genQueueRef.current.shift();
      // eslint-disable-next-line no-await-in-loop
      await runGeneration(job);
    }
    genActiveRef.current = false;
    scheduleGenRefetch();
  }, [runGeneration, scheduleGenRefetch]);

  // Queue a generation for the active node against the chosen KB scope.
  const handleGenerateActiveNode = useCallback(
    ({
      kb_folder_ids = [],
      kb_file_keys = [],
      rfp_folder_ids = [],
      rfp_file_keys = [],
      kb_whole = false,
    } = {}) => {
      // Generate drafts the section family while the section is still empty, so
      // it targets `generateTarget` rather than the selected node (see
      // ProposalDraftStep). Assign / Mark-as-Done still use the selected node.
      const node = draftActiveNode?.generateTarget || draftActiveNode;
      if (!node?.targetId) {
        toast.error("Select a section to generate.");
        return;
      }
      const targetId = node.targetId;
      // Don't double-add if already running or queued.
      if (
        generatingTargets[targetId] ||
        genQueueRef.current.some((j) => j.targetId === targetId)
      )
        return;
      setGeneratingTargets((prev) => ({
        ...prev,
        [targetId]: { percent: 0, message: "Queued…" },
      }));
      genQueueRef.current.push({
        targetId,
        title: node.title || "",
        isSubsection: node.isSubsection,
        kb_folder_ids,
        kb_file_keys,
        rfp_folder_ids,
        rfp_file_keys,
        kb_whole,
      });
      processGenQueue();
    },
    [draftActiveNode, generatingTargets, processGenQueue]
  );

  // Regenerate the active node with a custom prompt (and optional KB scope). The
  // regenerate endpoint is synchronous, so we just spin then refetch. Callers are
  // fire-and-forget, so errors surface via toast and are never rethrown.
  const handleRegenerateActiveNode = useCallback(
    async ({
      customPrompt,
      kb_folder_ids = [],
      kb_file_keys = [],
      rfp_folder_ids = [],
      rfp_file_keys = [],
      kb_whole = false,
    }) => {
      const node = draftActiveNode;
      if (!node?.targetId) {
        toast.error("Select a section to regenerate.");
        return;
      }
      const targetId = node.targetId;
      if (generatingTargets[targetId]) return; // already in flight

      // Snapshot the current version so the completion toast can offer Undo.
      const previousHtml = findNodeResponse(
        proposalData?.analysis?.toc,
        targetId
      );
      const wasSubsection = !!node.isSubsection;

      const fid = folderId || projectId;
      const reqOrgId = Cookies.get("selected_org_id");
      const url = reqOrgId
        ? `api/v1/rfp/folders/${fid}/regenerate/?organization_id=${encodeURIComponent(
            reqOrgId
          )}`
        : `api/v1/rfp/folders/${fid}/regenerate/`;

      // No `percent` → editor overlay shows an indeterminate spinner until the
      // first progress/stream frame arrives.
      setGeneratingTargets((prev) => ({
        ...prev,
        [targetId]: { message: "Regenerating…" },
      }));
      // Async flow (mirrors runGeneration): the POST returns 202 + status_key
      // immediately; the Celery task reports progress, live drafting text and
      // completion over the upload-status socket.
      let ws = null;
      let timer = null;
      let settled = false;
      const finish = ({ refetch = false } = {}) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        if (ws) {
          try {
            ws.close();
          } catch (e) {
            /* noop */
          }
        }
        clearGeneratingTarget(targetId);
        if (refetch) fetchAnalysisData();
      };
      try {
        const res = await api.post(
          url,
          {
            target_id: targetId,
            custom_prompt: customPrompt,
            kb_folder_ids,
            kb_file_keys,
            rfp_folder_ids,
            rfp_file_keys,
            kb_whole,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (![200, 201, 202].includes(res.status)) {
          throw new Error(`Server returned ${res.status}`);
        }
        const statusKey = res.data?.status_key;
        const freshToken = await getFreshAccessToken();
        ws = new WebSocket(
          `${process.env.REACT_APP_WS_URL || "ws://localhost:8000"}/ws/upload-status/?token=${freshToken}`
        );
        // Safety net so a silent socket never wedges the spinner; refetch in
        // case the regeneration finished but no frame arrived.
        timer = setTimeout(() => finish({ refetch: true }), 5 * 60 * 1000);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (statusKey && data.pdf_key && data.pdf_key !== statusKey)
              return;
            if (data.type === "progress") {
              setGeneratingTargets((prev) =>
                prev[targetId]
                  ? {
                      ...prev,
                      [targetId]: {
                        ...prev[targetId],
                        percent:
                          typeof data.progress === "number"
                            ? data.progress
                            : prev[targetId].percent,
                        message: data.message || "Regenerating…",
                      },
                    }
                  : prev
              );
              return;
            }
            if (data.type === "stream") {
              const delta = data.metadata?.delta || "";
              if (!delta) return;
              setGeneratingTargets((prev) =>
                prev[targetId]
                  ? {
                      ...prev,
                      [targetId]: {
                        ...prev[targetId],
                        message: data.message || "Drafting the response…",
                        streamText: (prev[targetId].streamText || "") + delta,
                      },
                    }
                  : prev
              );
              return;
            }
            if (data.type === "completed") {
              // Paint the final body straight from the frame so the editor
              // doesn't sit empty/stale while the reconciliation refetch
              // (below) is still in flight.
              const finalBody = data.metadata?.body;
              if (typeof finalBody === "string") {
                setProposalData((prev) =>
                  applyNodeResponse(
                    prev,
                    targetId,
                    finalBody,
                    data.metadata?.sources
                  )
                );
              }
              // Payoff + reversibility: offer to restore the pre-rewrite
              // version for a few seconds instead of a silent swap.
              if (previousHtml) {
                toast.success(
                  (tst) => (
                    <span className="flex items-center gap-3">
                      Section rewritten.
                      <button
                        onClick={async () => {
                          toast.dismiss(tst.id);
                          try {
                            await api.patch(
                              `api/v1/rfp/folders/${fid}/analysis/edit/`,
                              wasSubsection
                                ? {
                                    subsection_id: targetId,
                                    updated_response: previousHtml,
                                  }
                                : {
                                    section_id: targetId,
                                    updated_response: previousHtml,
                                  },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            await fetchAnalysisData();
                            toast.success("Previous version restored.");
                          } catch (e) {
                            toast.error(
                              "Couldn't restore the previous version."
                            );
                          }
                        }}
                        className="underline font-medium text-[#A04A97]"
                      >
                        Undo
                      </button>
                    </span>
                  ),
                  { duration: 10000 }
                );
              } else {
                toast.success("Content regenerated.");
              }
              finish({ refetch: true });
            } else if (data.type === "error" || data.error) {
              toast.error(data.message || "Failed to regenerate.");
              finish();
            }
          } catch (e) {
            /* ignore malformed frames */
          }
        };
        // A quiet close (proxy idle-timeout, missed "completed" frame) fires
        // onclose without onerror — refetch so saved content still maps.
        ws.onerror = () => finish({ refetch: true });
        ws.onclose = () => finish({ refetch: true });
      } catch (error) {
        console.error("Regenerate failed:", error);
        toast.error(error.response?.data?.error || "Failed to regenerate.");
        finish();
      }
    },
    [
      draftActiveNode,
      generatingTargets,
      proposalData,
      folderId,
      projectId,
      token,
      fetchAnalysisData,
      clearGeneratingTarget,
    ]
  );

  // A copilot-approved draft runs the same drafting pipeline as regenerate —
  // the consumer announces its status_key via a "draft_streaming" frame, and
  // we follow it on the upload-status socket so the editor shows the same
  // progress + live streaming as a normal generate/regenerate.
  const handleCopilotDraftStream = useCallback(
    async (statusKey, rawTargetId) => {
      if (!statusKey || !rawTargetId) return;
      const targetId = findNodeId(proposalData?.analysis?.toc, rawTargetId);
      if (generatingTargets[targetId]) return; // already tracked
      setGeneratingTargets((prev) => ({
        ...prev,
        [targetId]: { message: "Drafting from Copilot…" },
      }));
      let ws = null;
      let timer = null;
      let settled = false;
      const finish = ({ refetch = false } = {}) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        if (ws) {
          try {
            ws.close();
          } catch (e) {
            /* noop */
          }
        }
        clearGeneratingTarget(targetId);
        if (refetch) fetchAnalysisData();
      };
      try {
        const freshToken = await getFreshAccessToken();
        ws = new WebSocket(
          `${process.env.REACT_APP_WS_URL || "ws://localhost:8000"}/ws/upload-status/?token=${freshToken}`
        );
        timer = setTimeout(() => finish({ refetch: true }), 5 * 60 * 1000);
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.pdf_key && data.pdf_key !== statusKey) return;
            if (data.type === "progress") {
              setGeneratingTargets((prev) =>
                prev[targetId]
                  ? {
                      ...prev,
                      [targetId]: {
                        ...prev[targetId],
                        percent:
                          typeof data.progress === "number"
                            ? data.progress
                            : prev[targetId].percent,
                        message: data.message || "Drafting from Copilot…",
                      },
                    }
                  : prev
              );
              return;
            }
            if (data.type === "stream") {
              const delta = data.metadata?.delta || "";
              if (!delta) return;
              setGeneratingTargets((prev) =>
                prev[targetId]
                  ? {
                      ...prev,
                      [targetId]: {
                        ...prev[targetId],
                        message: data.message || "Drafting the response…",
                        streamText: (prev[targetId].streamText || "") + delta,
                      },
                    }
                  : prev
              );
              return;
            }
            if (data.type === "completed") {
              finish({ refetch: true });
            } else if (data.type === "error" || data.error) {
              finish();
            }
          } catch (e) {
            /* ignore malformed frames */
          }
        };
        ws.onerror = () => finish({ refetch: true });
        ws.onclose = () => finish({ refetch: true });
      } catch (e) {
        finish();
      }
    },
    [proposalData, generatingTargets, fetchAnalysisData, clearGeneratingTarget]
  );

  // This modal is now only ever a fresh generation — regenerating (with or
  // without new sources) goes through RegenerateInstructionsModal, which owns
  // both the scope picker and the instructions field.
  const handleScopeModalGenerate = useCallback(
    (scope = {}) => handleGenerateActiveNode(scope),
    [handleGenerateActiveNode]
  );

  // Persist an edited node. We store editor HTML directly so all formatting
  // survives — Markdown would lose underline/strikethrough/alignment/links/colour.
  const handleSaveActiveNode = useCallback(
    async ({ targetId, isSubsection, html }) => {
      if (!targetId) return false;
      const fid = folderId || projectId;
      try {
        await api.patch(
          `api/v1/rfp/folders/${fid}/analysis/edit/`,
          {
            ...(isSubsection
              ? { subsection_id: targetId }
              : { section_id: targetId }),
            updated_response: html || "",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchAnalysisData();
        toast.success("Changes saved.");
        return true;
      } catch (error) {
        console.error("Save failed:", error);
        toast.error(error.response?.data?.error || "Failed to save changes.");
        return false;
      }
    },
    [folderId, projectId, token, fetchAnalysisData]
  );

  // Persist an edited Generation Prompt (the section/subsection query) from the
  // generate modal. Sections send `section_query`, subsections `subsection_query`
  // — the backend keys off the node's parent to store it correctly. We refetch so
  // the modal reflects the saved value (and clears its dirty state).
  const handleSaveActiveNodeQuery = useCallback(
    async (newQuery) => {
      const node = draftActiveNode;
      if (isReviewer || !node?.targetId) return false;
      const fid = folderId || projectId;
      try {
        await api.patch(
          `api/v1/rfp/folders/${fid}/analysis/edit/`,
          node.isSubsection
            ? { subsection_id: node.targetId, subsection_query: newQuery }
            : { section_id: node.targetId, section_query: newQuery },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Generation prompt saved.");
        await fetchAnalysisData();
        return true;
      } catch (error) {
        console.error("Generation prompt update failed:", error);
        toast.error(error.response?.data?.error || "Failed to save prompt.");
        return false;
      }
    },
    [draftActiveNode, isReviewer, folderId, projectId, token, fetchAnalysisData]
  );

  // Draft sidebar row-menu actions (the "•••" hover menu).
  // Generate: node is already active, so open the same modal the step bar uses.
  const handleDraftGenerateNode = useCallback(() => {
    if (isReviewer) return;
    setGenerateSectionModalOpen(true);
  }, [isReviewer]);

  // Assign: open the Assign-members modal targeted explicitly at the chosen node.
  const handleDraftAssignNode = useCallback(
    (node) => {
      if (isReviewer || !node?.targetId) return;
      setActiveAssignSection({
        idx: null,
        title: node.title || "",
        sectionId: node.targetId,
        assignedMembers: node.assignedMembers || [],
      });
      setIsAssignModalOpen(true);
    },
    [isReviewer]
  );

  // Edit: persist a renamed title. Draft shows it optimistically; caller refreshes
  // on success and rolls back on failure.
  const handleDraftUpdateTitle = useCallback(
    async (node, newTitle) => {
      if (isReviewer || !node?.targetId) return false;
      const fid = folderId || projectId;
      try {
        await api.patch(
          `api/v1/rfp/folders/${fid}/analysis/edit/`,
          node.isSubsection
            ? { subsection_id: node.targetId, updated_subsection: newTitle }
            : { section_id: node.targetId, updated_section_text: newTitle },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchAnalysisData();
        return true;
      } catch (error) {
        console.error("Title update failed:", error);
        toast.error(error.response?.data?.error || "Failed to update title.");
        return false;
      }
    },
    [isReviewer, folderId, projectId, token, fetchAnalysisData]
  );

  // Delete: open the shared confirm-delete modal targeted at this node.
  const handleDraftDeleteNode = useCallback(
    (node) => {
      if (isReviewer || !node?.targetId) return;
      const noop = { stopPropagation: () => {} };
      if (node.isSubsection) {
        handleSubsectionDeleteClick(node.parentTargetId, node.targetId, noop);
      } else {
        handleSectionDeleteClick(node.targetId, noop);
      }
    },
    [isReviewer]
  );

  // Save to Knowledge Base as a Content Library entry. The backend resolves the
  // content from the saved response, so we only send the node id.
  const handleDraftSaveToKbNode = useCallback(
    async (node) => {
      if (isReviewer || !node?.targetId) return false;
      if (!node.content) {
        toast.error("This section has no content to save yet.");
        return false;
      }
      const fid = folderId || projectId;
      try {
        await toast.promise(
          api.post(
            `api/v1/rfp/folders/${fid}/content-library/`,
            node.isSubsection
              ? { subsection_id: node.targetId }
              : { section_id: node.targetId },
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          {
            loading: "Saving to Knowledge Base…",
            success: "Saved to Knowledge Base.",
            error: (e) =>
              e?.response?.data?.error || "Failed to save to Knowledge Base.",
          }
        );
        return true;
      } catch (error) {
        return false;
      }
    },
    [isReviewer, folderId, projectId, token]
  );

  // Close any open generation sockets/timers when leaving the page.
  useEffect(() => {
    const sockets = draftGenWsListRef;
    return () => {
      sockets.current.forEach((ws) => {
        try {
          ws.close();
        } catch (e) {
          /* noop */
        }
      });
      sockets.current = [];
      if (genRefetchTimerRef.current) clearTimeout(genRefetchTimerRef.current);
    };
  }, []);

  // Lazily load the section list on first reaching Draft/Review/Export. On Draft
  // we ensure the TOC is generated first; Review only reads it back (no generation).
  const draftFetchedRef = useRef(false);
  useEffect(() => {
    if (
      currentStep !== "draft" &&
      currentStep !== "review" &&
      currentStep !== "export"
    )
      return;
    if (draftFetchedRef.current) return;
    if (!folderId) return;
    if (proposalData?.analysis?.toc?.length) return;
    draftFetchedRef.current = true;
    setDraftLoading(true);
    const load =
      currentStep === "draft"
        ? ensureAnalysis().then(() => fetchAnalysisData())
        : Promise.resolve(fetchAnalysisData());
    load.finally(() => setDraftLoading(false));
  }, [currentStep, folderId, proposalData, ensureAnalysis, fetchAnalysisData]);

  const connectWebSocket = async () => {
    try {
      const freshToken = await getFreshAccessToken();
      const wsUrl = `${
        process.env.REACT_APP_WS_URL || "ws://localhost:8000"
      }/ws/upload-status/?token=${freshToken}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        generationStartedRef.current = true;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "completed") {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.close();
              wsRef.current = null;
            }

            generationStartedRef.current = false;

            if (generatingSubsectionRef.current) {
              setGeneratingSubsectionId(null);
              generatingSubsectionRef.current = null;
            } else {
              setGenerateLoading(false);
              setGenerateSuccess(true);
            }

            fetchAnalysisData();

            if (wsCompleteCallbackRef.current) {
              wsCompleteCallbackRef.current();
              wsCompleteCallbackRef.current = null;
              wsErrorCallbackRef.current = null;
            }

            if (!generatingSubsectionRef.current) {
              setTimeout(() => {
                showActionButtonsRef.current = false;
                setGenerateSuccess(false);
                setSelectedItems({ sectionIds: [], subsectionIds: [] });
              }, 3000);
            }
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);

        if (!generationStartedRef.current) {
          toast.error("Failed to connect to progress monitor");
        }

        if (generatingSubsectionRef.current) {
          setGeneratingSubsectionId(null);
          generatingSubsectionRef.current = null;
        }

        if (wsErrorCallbackRef.current) {
          wsErrorCallbackRef.current(error);
          wsErrorCallbackRef.current = null;
          wsCompleteCallbackRef.current = null;
        }
      };

      ws.onclose = () => {
        if (generationStartedRef.current) {
          console.log("WebSocket closed but generation might still be in progress");
        }

        if (wsErrorCallbackRef.current) {
          wsErrorCallbackRef.current(new Error("WebSocket closed prematurely"));
          wsErrorCallbackRef.current = null;
          wsCompleteCallbackRef.current = null;
        }
      };
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
      if (wsErrorCallbackRef.current) {
        wsErrorCallbackRef.current(error);
        wsErrorCallbackRef.current = null;
        wsCompleteCallbackRef.current = null;
      }
    }
  };

  const handleGenerateSection = async (e, sectionId) => {
    e.stopPropagation();
    if (isReviewer) return;
    wsCompleteCallbackRef.current = null;
    wsErrorCallbackRef.current = null;

    try {
      // generate-section-content/ was removed; generate-subsections/ handles a
      // single section_id (generates that section's intro only).
      const orgId = Cookies.get("selected_org_id");
      const url = orgId
        ? `api/v1/rfp/folders/${projectId}/generate-subsections/?organization_id=${encodeURIComponent(
            orgId
          )}`
        : `api/v1/rfp/folders/${projectId}/generate-subsections/`;

      const response = await api.post(
        url,
        { section_id: sectionId.toString() },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        await connectWebSocket();

        return new Promise((resolve, reject) => {
          wsCompleteCallbackRef.current = () => {
            setExpandedSections((prev) => ({ ...prev, [sectionId]: true }));
            setCollapsedSectionContents((prev) => ({ ...prev, [sectionId]: false }));
            resolve();
          };

          wsErrorCallbackRef.current = (err) => {
            toast.error("Generation failed or interrupted.");
            reject(err);
          };
        });
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Generate section error:", error);
      toast.error("Failed to generate section content");
      throw error;
    }
  };

  const handleIndividualSubsectionGenerate = async (subsectionId) => {
    if (isReviewer) return;
    if (!subsectionId) {
      toast.error("Invalid subsection");
      return;
    }

    setGeneratingSubsectionId(subsectionId);
    generatingSubsectionRef.current = subsectionId;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const generateData = {
        section_id: [],
        subsection_ids: [subsectionId],
      };

      const orgId = Cookies.get("selected_org_id");
      const url = orgId
        ? `api/v1/rfp/folders/${folderId}/generate-subsections/?organization_id=${encodeURIComponent(
            orgId
          )}`
        : `api/v1/rfp/folders/${folderId}/generate-subsections/`;

      const response = await api.post(
        url,
        generateData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        await connectWebSocket();
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error("Individual subsection generate error:", error);

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
        wsRef.current = null;
      }

      setGeneratingSubsectionId(null);
      generatingSubsectionRef.current = null;

      let errorMessage = "Failed to start generation";
      if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;

      toast.error(errorMessage);
    }
  };

  // Shared core for the inline Regenerate dropdowns — sections and subsections
  // hit the same unified /regenerate/ endpoint (it resolves target_id to either);
  // only the spinner state and messaging differ.
  const runInlineRegenerate = async (option, targetId, isSubsection) => {
    if (isReviewer) return;
    if (!targetId) {
      toast.error(isSubsection ? "Invalid subsection" : "Invalid section");
      return;
    }

    const setSpinner = (id) => {
      if (isSubsection) {
        setGeneratingSubsectionId(id);
        generatingSubsectionRef.current = id;
      } else {
        setGeneratingSectionId(id);
      }
    };
    setSpinner(targetId);

    try {
      const regenerateData = {
        target_id: String(targetId),
        custom_prompt: option.customInstructions || option.label,
      };

      const orgId = Cookies.get("selected_org_id");
      const url = orgId
        ? `api/v1/rfp/folders/${folderId}/regenerate/?organization_id=${encodeURIComponent(
            orgId
          )}`
        : `api/v1/rfp/folders/${folderId}/regenerate/`;

      const response = await api.post(
        url,
        regenerateData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        await fetchAnalysisData();
        setSpinner(null);
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error("Inline regeneration error:", error);
      setSpinner(null);

      let errorMessage = isSubsection
        ? "Failed to start regeneration"
        : "Failed to start section regeneration";
      if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;

      toast.error(errorMessage);
    }
  };

  const handleInlineRegenerateSelect = (option, subsectionId) =>
    runInlineRegenerate(option, subsectionId, true);

  const handleInlineSectionRegenerateSelect = (option, sectionId) =>
    runInlineRegenerate(option, sectionId, false);

  const handleViewSourcesClick = (sources, e) => {
    e.stopPropagation();
    if (!sources || sources.length === 0) {
      toast.error("No sources available for this content.", { icon: "ℹ️" });
      return;
    }
    setCurrentSources(sources);
    setIsSourcesListOpen(true);
  };

  const handleSelectSource = (source) => {
    setSelectedSourceData({
      url: source.file_url,
      name: source.file_name,
      page: source.page_number !== undefined ? source.page_number : source.page,
      highlightText: source.source_chunk || source.snippet
    });
    setIsSourcesListOpen(false);
    setTimeout(() => {
      setIsSourceViewOpen(true);
    }, 150);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleBackClick = () => {
    navigate(`/rfp-projects/project/${projectId}`);
  };

  const calculateProgress = (section) => {
    if (!section?.children || section.children.length === 0)
      return { completed: 0, total: 0, percentage: 0 };
    const completed = section.children.filter((child) => child?.response).length;
    const total = section.children.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  const handleSectionCheckboxChange = (sectionId, isChecked) => {
    if (isReviewer) return;
    const section = proposalData?.analysis?.toc?.find((s) => s?.id === sectionId);
    if (!section) return;

    const allSubsectionsGenerated =
      section.children && section.children.length > 0 && section.children.every((child) => child?.response);

    if (allSubsectionsGenerated) return;

    const subsectionIds = section.children?.filter((child) => !child?.response).map((child) => child.id) || [];

    setSelectedItems((prev) => {
      let newSectionIds;
      let newSubsectionIds;

      if (isChecked) {
        newSectionIds = [...prev.sectionIds, sectionId];
        newSubsectionIds = [...prev.subsectionIds, ...subsectionIds];
      } else {
        newSectionIds = prev.sectionIds.filter((id) => id !== sectionId);
        newSubsectionIds = prev.subsectionIds.filter((id) => !subsectionIds.includes(id));
      }

      newSubsectionIds = [...new Set(newSubsectionIds)];

      return {
        ...prev,
        sectionIds: newSectionIds,
        subsectionIds: newSubsectionIds,
      };
    });
  };

  const handleSubsectionCheckboxChange = (subsectionId, isChecked) => {
    if (isReviewer) return;
    let targetSubsection = null;
    proposalData?.analysis?.toc?.forEach((section) => {
      if (section?.children) {
        const subsection = section.children.find((child) => child?.id === subsectionId);
        if (subsection) targetSubsection = subsection;
      }
    });

    if (targetSubsection?.response) return;

    setSelectedItems((prev) => {
      let parentSectionId = null;
      proposalData?.analysis?.toc?.forEach((section) => {
        if (section?.children?.some((child) => child?.id === subsectionId)) {
          parentSectionId = section.id;
        }
      });

      const newSubsectionIds = isChecked
        ? [...prev.subsectionIds, subsectionId]
        : prev.subsectionIds.filter((id) => id !== subsectionId);

      let newSectionIds = [...prev.sectionIds];

      if (parentSectionId) {
        const section = proposalData?.analysis?.toc?.find((s) => s?.id === parentSectionId);
        const eligibleSubsections = section?.children?.filter((child) => !child?.response) || [];
        const sectionSubsectionIds = eligibleSubsections.map((child) => child.id);

        const allEligibleSubsectionsSelected =
          sectionSubsectionIds.length > 0 && sectionSubsectionIds.every((id) => newSubsectionIds.includes(id));

        if (allEligibleSubsectionsSelected && !prev.sectionIds.includes(parentSectionId)) {
          newSectionIds = [...newSectionIds, parentSectionId];
        } else if (!allEligibleSubsectionsSelected && prev.sectionIds.includes(parentSectionId)) {
          newSectionIds = newSectionIds.filter((id) => id !== parentSectionId);
        }
      }

      return {
        sectionIds: newSectionIds,
        subsectionIds: newSubsectionIds,
      };
    });
  };

  const calculateSectionCheckboxState = (section) => {
    if (!section) return false;
    const allSubsectionsGenerated =
      section.children && section.children.length > 0 && section.children.every((child) => child?.response);

    if (allSubsectionsGenerated) return false;

    const isExplicitlySelected = selectedItems.sectionIds.includes(section.id);

    if (!section.children || section.children.length === 0) return isExplicitlySelected;

    const eligibleSubsections = section.children.filter((child) => !child?.response);
    const sectionSubsectionIds = eligibleSubsections.map((child) => child.id);
    const allEligibleSubsectionsSelected =
      sectionSubsectionIds.length > 0 && sectionSubsectionIds.every((id) => selectedItems.subsectionIds.includes(id));

    return isExplicitlySelected || allEligibleSubsectionsSelected;
  };

  const hasSelectedItems = useMemo(() => {
    return selectedItems.sectionIds.length > 0 || selectedItems.subsectionIds.length > 0;
  }, [selectedItems.sectionIds, selectedItems.subsectionIds]);

  const selectedSubsectionCount = useMemo(() => {
    return selectedItems.subsectionIds.length;
  }, [selectedItems.subsectionIds]);

  const hasSelectedSubsections = selectedSubsectionCount > 0;

  const handleAddSection = (sectionId) => {
    if (isReviewer) return;
    const section = proposalData?.analysis?.toc?.find((s) => s?.id === sectionId);
    setCurrentSectionId({
      id: sectionId,
      previousIndex: section ? parseInt(section.number) : 0,
    });
    setIsAddSectionModalOpen(true);
  };

  const handleAddSectionSubmit = () => {
    // The modal performs the POST itself; refetch so the new section shows.
    fetchAnalysisData();
  };

  // Insert a new top-level section at the given array position (previous_index).
  const handleAddSectionAt = useCallback(
    (previousIndex) => {
      if (isReviewer) return;
      setCurrentSectionId({ previousIndex });
      setIsAddSectionModalOpen(true);
    },
    [isReviewer]
  );

  const handleSectionDeleteClick = (sectionId, e) => {
    e.stopPropagation();
    if (isReviewer) return;
    setDeleteTarget({ type: "section", sectionId: sectionId, subsectionId: null });
    setIsDeleteModalOpen(true);
  };

  const handleGenerateProposalModal = (shouldOpen) => {
    if (isReviewer) return;
    setIsGenerationModalOpen(shouldOpen);
  };

  const handleSubsectionDeleteClick = (sectionId, subsectionId, e) => {
    e.stopPropagation();
    if (isReviewer) return;
    setDeleteTarget({ type: "subsection", sectionId: sectionId, subsectionId: subsectionId });
    setIsDeleteModalOpen(true);
  };

  const handleBulkDeleteClick = () => {
    if (isReviewer) return;
    const { sectionIds, subsectionIds } = selectedItems;
    if (sectionIds.length === 0 && subsectionIds.length === 0) {
      toast.error("Please select items to delete");
      return;
    }
    setDeleteTarget({ type: "selected", sectionId: null, subsectionId: null });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (isReviewer) return;
    setDeleteLoading(true);
    try {
      let sectionIds = [];
      let subsectionIds = [];

      if (deleteTarget.type === "section") sectionIds = [deleteTarget.sectionId];
      else if (deleteTarget.type === "subsection") subsectionIds = [deleteTarget.subsectionId];
      else if (deleteTarget.type === "selected") {
        sectionIds = [...selectedItems.sectionIds];
        subsectionIds = [...selectedItems.subsectionIds];
      }

      const requestBody = { section_ids: sectionIds, subsection_ids: subsectionIds };

      const response = await api.delete(`api/v1/rfp/folders/${folderId}/analysis/delete/`, {
        data: requestBody,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 204) {
        setIsDeleteModalOpen(false);
        setDeleteLoading(false);

        if (deleteTarget.type === "selected") setSelectedItems({ sectionIds: [], subsectionIds: [] });
        setDeleteTarget({ type: null, sectionId: null, subsectionId: null });
        await fetchAnalysisData();
      } else {
        throw new Error("Failed to delete items");
      }
    } catch (error) {
      console.error("Delete error:", error);
      setDeleteLoading(false);
    }
  };

  const handleDeleteModalClose = () => {
    if (!deleteLoading) {
      setIsDeleteModalOpen(false);
      setDeleteTarget({ type: null, sectionId: null, subsectionId: null });
    }
  };

  const getDeleteMessage = () => {
    // Draft sidebar passes stringified ids; match by id or number via String().
    const matchesTarget = (node, targetId) =>
      String(node?.id ?? "") === String(targetId) ||
      String(node?.number ?? "") === String(targetId);
    if (deleteTarget.type === "section") {
      const section = proposalData?.analysis?.toc?.find((s) =>
        matchesTarget(s, deleteTarget.sectionId)
      );
      return `section "${section?.title || "this section"}"`;
    } else if (deleteTarget.type === "subsection") {
      let subsectionTitle = "this subsection";
      proposalData?.analysis?.toc?.forEach((section) => {
        if (section?.children) {
          const subsection = section.children.find((child) =>
            matchesTarget(child, deleteTarget.subsectionId)
          );
          if (subsection) subsectionTitle = subsection.title;
        }
      });
      return `subsection "${subsectionTitle}"`;
    } else if (deleteTarget.type === "selected") {
      const totalCount = selectedItems.sectionIds.length + selectedItems.subsectionIds.length;
      return `${totalCount} selected item(s)`;
    }
    return "selected item(s)";
  };

  const prepareGenerateData = () => {
    const { sectionIds, subsectionIds } = selectedItems;
    const filteredSubsectionIds = subsectionIds.filter((subsectionId) => {
      let parentSection = null;
      proposalData?.analysis?.toc?.forEach((section) => {
        if (section?.children?.some((child) => child?.id === subsectionId)) {
          parentSection = section;
        }
      });
      return !parentSection || !sectionIds.includes(parentSection.id);
    });

    return { section_id: sectionIds, subsection_ids: filteredSubsectionIds };
  };

  const handleGenerateSections = async () => {
    if (isReviewer) return;
    if (!hasSelectedSubsections) {
      toast.error("Please select items to generate");
      return;
    }

    const generateData = prepareGenerateData();

    if (generateData.section_id.length === 0 && generateData.subsection_ids.length === 0) {
      toast.error("Please select valid items to generate");
      return;
    }

    setGenerateLoading(true);
    setGenerateSuccess(false);
    generationStartedRef.current = false;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const orgId = Cookies.get("selected_org_id");
      const url = orgId
        ? `api/v1/rfp/folders/${folderId}/generate-subsections/?organization_id=${encodeURIComponent(
            orgId
          )}`
        : `api/v1/rfp/folders/${folderId}/generate-subsections/`;

      const response = await api.post(
        url,
        generateData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        await connectWebSocket();
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.error("Generate sections error:", error);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
        wsRef.current = null;
      }
      generationStartedRef.current = false;

      let errorMessage = "Failed to start generation";
      if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;

      toast.error(errorMessage);
      setGenerateLoading(false);
      showActionButtonsRef.current = true;
    }
  };

  const proposalProgress = useMemo(() => {
    if (!proposalData?.analysis?.toc) return { completed: 0, total: 0 };
    const totalSections = proposalData.analysis.toc.length;
    let completedSections = 0;

    proposalData.analysis.toc.forEach((section) => {
      if (section?.children && section.children.length > 0) {
        const hasAtLeastOneResponse = section.children.some((subsection) => subsection?.response);
        if (hasAtLeastOneResponse) completedSections++;
      }
    });

    return { completed: completedSections, total: totalSections };
  }, [proposalData]);

  const isGenerating = generateLoading || generatingSubsectionId !== null;

  const handleGenerateProposalClick = () => {
    if (isReviewer) return;
    setIsGenerationModalOpen(true);
    setTimeout(() => {
      setIsGenerationModalOpen(false);
    }, 3000);
  };

  const handleAddSubsectionSubmit = async (data) => {
    console.log("Subsection added:", data);
    toast.success("Subsection added successfully!");
  };

  const handleAddSubsectionClick = (section, currentSubsectionIndex, e) => {
    e.stopPropagation();
    if (isReviewer) return;
    
    // If a specific subsection was clicked, insert after it.
    // If the section was clicked, append to the end by passing a dummy suffix (.9999)
    // so the backend sets insert_pos to len(children).
    const targetIndex = currentSubsectionIndex || `${section.number}.9999`;

    setCurrentSectionForSub({
      id: section.id,
      title: section.title,
      lastSubsectionIndex: targetIndex,
    });

    setIsAddSubsectionModalOpen(true);
  };

  const toggleSubsectionContent = (subsectionId) => {
    setExpandedContentSubsections((prev) => ({
      ...prev,
      [subsectionId]: !prev[subsectionId],
    }));
  };

  const handleCopy = (id, content, type) => {
    if (!content) return;
    const textToCopy = typeof content === "string" ? content : JSON.stringify(content, null, 2);
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        setCopiedId(`${type}-${id}`);
        setTimeout(() => {
          setCopiedId((currentId) => currentId === `${type}-${id}` ? null : currentId);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        toast.error("Failed to copy to clipboard");
      });
  };

  const handleOpenAssignModal = (sectionIdx, sectionTitle, sectionId, assignedMembers = []) => {
    setActiveAssignSection({ idx: sectionIdx, title: sectionTitle, sectionId, assignedMembers });
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setActiveAssignSection({ idx: null, title: "", sectionId: null });
  };

  const shouldShowActionButtons =
    showActionButtonsRef.current &&
    (selectedSubsectionCount > 0 || generateLoading || generateSuccess);

  return (
    <div className="h-full bg-[#FAF9FB] flex flex-col w-full">
      {showOnboarding && !loading && currentStep === "setup" && (
        <CreateProposalOnboardingModal
          onGetStarted={dismissOnboarding}
          onGoBack={() => {
            dismissOnboarding();
            handleBackClick();
          }}
        />
      )}

      <div className="flex-shrink-0">
        <Header
          icon={"/arrow-back.svg"}
          name={`Proposal for ${projectName}` || `Proposal for ${proposalData?.folder_name}` || "Project Proposal"}
          onClick={handleBackClick}
          isProposalPage={true}
          proposalProgress={proposalProgress}
          isGenerating={isGenerating}
          onGenerateProposal={handleGenerateProposalModal}
          folderId={folderId}
          projectName={projectName}
          dueDate={deadline}
          onTeamMembersClick={() => setIsTeamMembersModalOpen(true)}
          isReviewer={isReviewer}
        />
      </div>

      <AddSectionModal
        isOpen={isAddSectionModalOpen && !isReviewer}
        onClose={() => setIsAddSectionModalOpen(false)}
        onAddSection={handleAddSectionSubmit}
        folderId={folderId}
        previousIndex={currentSectionId?.previousIndex || 0}
        refreshAnalysisData={fetchAnalysisData}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen && !isReviewer}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteConfirm}
        fileName={getDeleteMessage()}
        loading={deleteLoading}
        deleteSuccess={false}
      />

      <AddSubsectionModal
        isOpen={isAddSubsectionModalOpen && !isReviewer}
        onClose={() => {
          setIsAddSubsectionModalOpen(false);
          setCurrentSectionForSub(null);
        }}
        onSubmit={handleAddSubsectionSubmit}
        sectionTitle={currentSectionForSub?.title || ""}
        sectionId={currentSectionForSub?.id}
        folderId={folderId}
        previousIndex={currentSectionForSub?.lastSubsectionIndex || "0"}
        token={token}
        refreshAnalysisData={fetchAnalysisData}
      />

      <AssignMembersModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        sectionTitle={activeAssignSection.title}
        sectionId={activeAssignSection.sectionId}
        folderId={folderId || projectId}
        onAssignSuccess={fetchAnalysisData}
        assignedMembers={activeAssignSection.assignedMembers}
      />

      <ProposalStepBar
        currentStep={currentStep}
        reachedIndex={reachedStepIndex}
        onStepClick={handleStepClick}
        onContinue={handleContinueStep}
        actions={
          currentStep === "draft" && !isReviewer ? (
            <>
              {/* All four per-section actions — Generate, Regenerate, Assign and
                  Mark as Done — now live on the hover toolbar over each section
                  in the document, where they act on the node you're pointing at
                  rather than on "whatever is selected". Kept verbatim below so
                  they can be restored by uncommenting.

              {draftActiveNode?.hasContent ? (
                <RegenerateMenu
                  isSubsection={!!draftActiveNode?.isSubsection}
                  disabled={
                    !draftActiveNode?.targetId ||
                    !!generatingTargets[draftActiveNode?.targetId]
                  }
                  onRegenerate={() => setCustomInstructionOpen(true)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setGenerateSectionModalOpen(true)}
                  className="px-3 py-1 rounded-md border border-[#E5E7EB] text-[#374151] font-outfit text-[14px] font-medium hover:bg-gray-50 transition-colors"
                >
                  Generate
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!draftActiveNode?.targetId) return;
                  setActiveAssignSection({
                    idx: null,
                    title: draftActiveNode.title || "",
                    sectionId: draftActiveNode.targetId,
                    assignedMembers: draftActiveNode.assignedMembers || [],
                  });
                  setIsAssignModalOpen(true);
                }}
                disabled={!draftActiveNode?.targetId}
                className="px-3 py-1 rounded-md border border-[#E5E7EB] text-[#374151] font-outfit text-[14px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {draftActiveNode?.isSubsection ? "Assign Subsection" : "Assign Section"}
              </button>
              {(!activeNodeDone || markDoneStatus !== "idle") && (
                <button
                  type="button"
                  onClick={handleMarkActiveDone}
                  disabled={markDoneStatus !== "idle" || !draftActiveNode}
                  className="px-3 py-1 rounded-md border border-[#E5E7EB] text-[#374151] font-outfit text-[14px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {markDoneStatus === "loading"
                    ? "Marking…"
                    : markDoneStatus === "success"
                    ? "Done ✓"
                    : "Mark as Done"}
                </button>
              )}
              */}
            </>
          ) : currentStep === "export" && exportControls ? (
            <button
              type="button"
              onClick={exportControls.onExport}
              disabled={exportControls.disabled}
              className="flex items-center gap-1.5 px-4 py-1 rounded-md border border-[#A04A97] text-[#A04A97] font-outfit text-[14px] font-medium hover:bg-[#C95EBE0D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportControls.exporting ? (
                <>
                  <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-[#A04A97]" />
                  Exporting…
                </>
              ) : (
                "Export Document"
              )}
            </button>
          ) : null
        }
      />

      <div className={`relative flex flex-1 overflow-hidden mb-5 ${["review", "setup"].includes(currentStep) ? "pl-5 gap-6" : "pr-4 pl-5 pt-4"}`}>
        <div className={`flex-1 overflow-hidden ${["review", "setup"].includes(currentStep) ? "pt-4" : ""}`}>
          {loading ? (
            <div className="h-full overflow-hidden flex flex-col gap-4 p-6">
              <div className="h-6 w-1/2 rounded bg-gray-200 animate-pulse" />
              <div className="flex items-center gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-6 w-6 rounded bg-gray-200 animate-pulse" />
                ))}
              </div>
              <div className="space-y-3 mt-2">
                {[92, 98, 85, 95, 70, 90, 99, 60, 88, 80].map((w, i) => (
                  <div
                    key={i}
                    className="h-3.5 rounded bg-gray-200 animate-pulse"
                    style={{ width: `${w}%` }}
                  />
                ))}
              </div>
            </div>
          ) : currentStep === "setup" ? (
            <ProposalSetupStep
              selectedTone={selectedTone}
              setSelectedTone={handleToneChange}
              selectedLength={selectedLength}
              setSelectedLength={handleLengthChange}
              usps={usps}
              setUsps={handleUspsChange}
              customInstructions={customInstructions}
              setCustomInstructions={handleCustomInstructionsChange}
              selectedCompanionDocs={selectedCompanionDocs}
              toggleCompanionDoc={toggleCompanionDoc}
              saveStatus={saveStatus}
              onRemoveUsp={handleRemoveUsp}
              customDocs={customDocs}
              onCreateCustomDoc={handleCreateCustomDoc}
              onToggleCustomDoc={handleToggleCustomDoc}
              structureSource={structureSource}
              onSelectAiStructure={handleSelectAiStructure}
              onChooseTemplateSource={handleChooseTemplateSource}
              selectedTemplateId={selectedTemplateId}
              selectedTemplateName={selectedTemplateName}
              templateUnavailable={templateUnavailable}
              templates={templates}
              templatesLoading={templatesLoading}
              templatesError={templatesError}
              onReloadTemplates={loadTemplates}
              onPickTemplate={handlePickTemplate}
              onViewAllTemplates={handleViewAllTemplates}
            />
          ) : currentStep === "draft" && analysisBlocked ? (
            // Nothing ingested to build from — say so instead of running a build
            // that could only produce an empty outline.
            <ProposalNotReadyView
              blocked={analysisBlocked}
              onGoToDocuments={() => navigate(projectFilesPath)}
            />
          ) : currentStep === "draft" ? (
            // On first run the build view sits ON the draft page (blurred
            // behind) rather than replacing it, so the workspace you're about to
            // land in stays visible the whole time.
            <div className="relative h-full">
            <ProposalDraftStep
              apiSections={proposalData?.analysis?.toc}
              loading={draftLoading}
              folderId={folderId || projectId}
              onScopeClick={() => setGenerateSectionModalOpen(true)}
              onActiveNodeChange={setDraftActiveNode}
              doneOverrides={doneOverrides}
              generatingTargets={generatingTargets}
              onSave={handleSaveActiveNode}
              onAddSectionAt={isReviewer ? undefined : handleAddSectionAt}
              onAddSubsection={isReviewer ? undefined : (section, subNumber) => handleAddSubsectionClick(section, subNumber, { stopPropagation() {} })}
              isReviewer={isReviewer}
              onGenerateNode={handleDraftGenerateNode}
              onRegenerateNode={() => setCustomInstructionOpen(true)}
              onMarkNodeDone={handleMarkActiveDone}
              onAssignNode={handleDraftAssignNode}
              onSaveToKbNode={handleDraftSaveToKbNode}
              onDeleteNode={handleDraftDeleteNode}
              onUpdateTitle={handleDraftUpdateTitle}
            />
              {analysisProgress && (
                <ProposalBuildingView
                  progress={analysisProgress}
                  documentCount={proposalData?.analysis?.document_count || 0}
                />
              )}
            </div>
          ) : currentStep === "review" ? (
            <ProposalReviewStep
              apiSections={proposalData?.analysis?.toc}
              loading={draftLoading}
              onEditNode={() => goToStep("draft")}
              onReordered={fetchAnalysisData}
            />
          ) : (
            <ProposalExportStep
              apiSections={proposalData?.analysis?.toc}
              projectName={projectName}
              folderId={folderId || projectId}
              onExportControlsChange={setExportControls}
            />
          )}
        </div>

        {(rightPanelOpen || currentStep === "setup") && (
          <ProposalCopilotPanel
            tab={rightTab}
            setTab={setRightTab}
            onClose={() => setRightPanelOpen(false)}
            hideClose={currentStep === "setup"}
            copilotMessages={copilotMessages}
            setCopilotMessages={setCopilotMessages}
            copilotChatId={copilotChatId}
            setCopilotChatId={setCopilotChatId}
            activeSectionId={
              currentStep === "draft" ? draftActiveNode?.targetId || null : null
            }
            activeSectionIsSubsection={
              currentStep === "draft" ? !!draftActiveNode?.isSubsection : false
            }
            onRefreshProposal={fetchAnalysisData}
            onDraftStreaming={handleCopilotDraftStream}
            showCoverage={currentStep === "draft"}
            folderId={folderId || projectId}
            noTopBorder={["review", "setup"].includes(currentStep)}
          />
        )}

        {/* Floating button to open Copilot / RFP Brief panel */}
        {!rightPanelOpen && currentStep !== "export" && currentStep !== "setup" && (
          <button
            onClick={() => {
              setRightTab("copilot");
              setRightPanelOpen(true);
            }}
            aria-label="Open Copilot and RFP Brief"
            style={{
              background: "linear-gradient(135deg, #C95EBE 0%, #963D8C 100%)",
            }}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full border border-[#D9D9D9] shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="26" viewBox="0 0 22 26" fill="none">
              <path
                d="M12.6474 6.32314V0H6.32406C5.76505 0 5.22893 0.222062 4.83365 0.617335C4.43836 1.01261 4.2163 1.54871 4.2163 2.10771V8.54748C4.52194 8.43985 4.84874 8.40614 5.16992 8.44913C5.4911 8.49211 5.79752 8.61057 6.06411 8.79479C6.40135 9.03366 6.65428 9.3709 6.7948 9.76574L7.28661 11.3002C7.3693 11.5493 7.50903 11.7758 7.69468 11.9614C7.88033 12.1471 8.10676 12.2868 8.35595 12.3695L9.84684 12.8613C10.2965 13.0172 10.6337 13.2702 10.8726 13.6088C11.1129 13.9601 11.2394 14.3535 11.2394 14.7624C11.2394 15.1713 11.1129 15.5648 10.874 15.9034C10.6211 16.2547 10.2839 16.5076 9.89041 16.6481L9.21451 16.873C9.55176 16.887 9.87495 17.0135 10.156 17.2116C10.4511 17.4224 10.69 17.7174 10.8164 18.0842L11.1832 19.2097C11.2253 19.3221 11.2815 19.4345 11.3799 19.5188C11.4783 19.6031 11.5766 19.6734 11.7031 19.7156L12.7724 20.0682C13.1799 20.2088 13.4891 20.4476 13.7139 20.7582C13.9247 21.0533 14.0525 21.4186 14.0525 21.8134C14.0525 22.0523 14.0113 22.2753 13.9289 22.4823H18.9707C19.5297 22.4823 20.0658 22.2602 20.4611 21.8649C20.8564 21.4697 21.0784 20.9336 21.0784 20.3746V8.43085H14.7551C14.1961 8.43085 13.66 8.20879 13.2647 7.81352C12.8694 7.41825 12.6474 6.88214 12.6474 6.32314ZM14.0525 6.32314V0.351286L20.7271 7.02571H14.7551C14.5688 7.02571 14.3901 6.95169 14.2583 6.81993C14.1266 6.68818 14.0525 6.50947 14.0525 6.32314ZM5.45004 10.2322L5.93904 11.7372C6.09069 12.1949 6.34733 12.6108 6.68842 12.9516C7.02952 13.2925 7.44563 13.5488 7.90348 13.7001L9.40983 14.1891L9.44075 14.1976C9.55682 14.2384 9.65735 14.3143 9.72847 14.4147C9.79958 14.5151 9.83777 14.6352 9.83777 14.7582C9.83777 14.8813 9.79958 15.0013 9.72847 15.1017C9.65735 15.2021 9.55682 15.278 9.44075 15.3189L7.9344 15.8079C7.47628 15.959 7.0599 16.2152 6.71854 16.5561C6.37719 16.8969 6.12034 17.313 5.96855 17.7708L5.47955 19.2743C5.43869 19.3904 5.36281 19.4909 5.26239 19.562C5.16196 19.6332 5.04194 19.6714 4.91889 19.6714C4.79583 19.6714 4.67581 19.6332 4.57538 19.562C4.47496 19.4909 4.39908 19.3904 4.35822 19.2743L3.86922 17.7708C3.69389 17.2387 3.37721 16.7641 2.95304 16.398C2.64594 16.1314 2.28923 15.928 1.90337 15.7994L0.397024 15.3104C0.280951 15.2696 0.180421 15.1937 0.109307 15.0933C0.0381924 14.9929 0 14.8728 0 14.7498C0 14.6267 0.0381924 14.5067 0.109307 14.4063C0.180421 14.3059 0.280951 14.23 0.397024 14.1891L1.90337 13.7001C2.35577 13.5449 2.76598 13.2868 3.10173 12.9462C3.43748 12.6056 3.68962 12.1917 3.8383 11.7372L4.32731 10.2337C4.36787 10.1171 4.44371 10.0161 4.54431 9.94455C4.6449 9.87305 4.76526 9.83463 4.88867 9.83463C5.01209 9.83463 5.13245 9.87305 5.23304 9.94455C5.33364 10.0161 5.40948 10.1171 5.45004 10.2337M12.3424 21.3778L11.2661 21.0294C10.9396 20.9203 10.643 20.7369 10.3996 20.4935C10.1562 20.2501 9.97276 19.9535 9.86371 19.627L9.51241 18.5521C9.48332 18.4691 9.42916 18.3972 9.35741 18.3463C9.28567 18.2954 9.19989 18.2681 9.11194 18.2681C9.02398 18.2681 8.9382 18.2954 8.86646 18.3463C8.79472 18.3972 8.74055 18.4691 8.71146 18.5521L8.36298 19.627C8.25591 19.9514 8.07545 20.2467 7.83563 20.4899C7.59581 20.7331 7.3031 20.9177 6.98028 21.0294L5.90532 21.3778C5.82296 21.4076 5.75176 21.462 5.70144 21.5336C5.65112 21.6053 5.62411 21.6907 5.62411 21.7783C5.62411 21.8659 5.65112 21.9513 5.70144 22.023C5.75176 22.0946 5.82296 22.149 5.90532 22.1788L6.98028 22.5287C7.30786 22.6379 7.60545 22.822 7.84938 23.0665C8.09331 23.3109 8.27687 23.6088 8.38546 23.9366L8.73394 25.0101C8.76304 25.0931 8.8172 25.165 8.88894 25.2159C8.96069 25.2668 9.04647 25.2941 9.13442 25.2941C9.22237 25.2941 9.30815 25.2668 9.3799 25.2159C9.45164 25.165 9.5058 25.0931 9.53489 25.0101L9.88479 23.9366C9.99379 23.6097 10.1774 23.3127 10.421 23.0691C10.6647 22.8255 10.9617 22.6419 11.2886 22.5329L12.3635 22.1844C12.4465 22.1553 12.5184 22.1011 12.5693 22.0294C12.6202 21.9577 12.6475 21.8719 12.6475 21.7839C12.6475 21.696 12.6202 21.6102 12.5693 21.5385C12.5184 21.4667 12.4465 21.4126 12.3635 21.3835L12.3424 21.3778Z"
                fill="url(#paint0_linear_5754_44267)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_5754_44267"
                  x1="10.5392"
                  y1="0"
                  x2="10.5392"
                  y2="25.2941"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="white" />
                  <stop offset="1" stopColor="#CCD1FF" />
                </linearGradient>
              </defs>
            </svg>
          </button>
        )}
      </div>

      <ProposalLoadingPopup isOpen={isGenerationModalOpen && !isReviewer} />

      <SourcesListModal
        isOpen={isSourcesListOpen}
        onClose={() => setIsSourcesListOpen(false)}
        sources={currentSources}
        onSelectSource={handleSelectSource}
      />

      <SourceViewModal
        isOpen={isSourceViewOpen}
        onClose={() => setIsSourceViewOpen(false)}
        data={selectedSourceData}
      />

      <TeamMembersModal
        isOpen={isTeamMembersModalOpen}
        onClose={() => {
          setIsTeamMembersModalOpen(false);
          setExpandedMemberIds(new Set()); 
          setRightViewTab("comments");     
          setIsDropdownOpen(false);        
        }}
        proposalData={proposalData} 
        rightViewTab={rightViewTab}
        setRightViewTab={setRightViewTab}
        filterType={filterType}
        setFilterType={setFilterType}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
        expandedMemberIds={expandedMemberIds}
        toggleMemberCard={toggleMemberCard}
        getInitials={getInitials}
        orgId={orgId}
        folderId={folderId}
        onRefresh={fetchAnalysisData}
        userPermission={proposalData?.user_permission || null}
      />

      <GenerateSectionModal
        isOpen={generateSectionModalOpen}
        onClose={() => setGenerateSectionModalOpen(false)}
        orgId={orgId}
        folderId={folderId || projectId}
        // Show the writing task for what will ACTUALLY be drafted: on an
        // undrafted subsection that is the parent section, since one call
        // writes the whole group.
        isSubsection={
          (draftActiveNode?.generateTarget || draftActiveNode)?.isSubsection || false
        }
        generationPrompt={(draftActiveNode?.generateTarget || draftActiveNode)?.query || ""}
        writingBrief={(draftActiveNode?.generateTarget || draftActiveNode)?.writingBrief || ""}
        onSavePrompt={isReviewer ? undefined : handleSaveActiveNodeQuery}
        initialKbScope={
          (draftActiveNode?.generateTarget || draftActiveNode)?.kbScope || null
        }
        initialRfpScope={
          (draftActiveNode?.generateTarget || draftActiveNode)?.rfpScope || null
        }
        onGenerate={handleScopeModalGenerate}
      />

      <RegenerateInstructionsModal
        isOpen={customInstructionOpen}
        onClose={() => setCustomInstructionOpen(false)}
        // The brief this node was actually drafted with — for a subsection that
        // came with its section, that is the SECTION-level prompt, since that is
        // what the backend wrote it from.
        generationPrompt={
          draftActiveNode?.effectivePrompt ||
          draftActiveNode?.writingBrief ||
          draftActiveNode?.query ||
          ""
        }
        orgId={orgId}
        folderId={folderId || projectId}
        isSubsection={draftActiveNode?.isSubsection || false}
        initialKbScope={draftActiveNode?.kbScope || null}
        initialRfpScope={draftActiveNode?.rfpScope || null}
        onRegenerate={({
          instructions,
          kb_folder_ids,
          kb_file_keys,
          rfp_folder_ids,
          rfp_file_keys,
          kb_whole,
        }) =>
          handleRegenerateActiveNode({
            customPrompt: instructions,
            kb_folder_ids,
            kb_file_keys,
            rfp_folder_ids,
            rfp_file_keys,
            kb_whole,
          })
        }
      />

      {/* The build runs server-side regardless of where you are, so once you
          leave the Draft step it minimizes to a pill rather than disappearing. */}
      {analysisProgress && currentStep !== "draft" && (
        <ProposalBuildingPill
          progress={analysisProgress}
          onOpen={() => goToStep("draft")}
        />
      )}
    </div>
  );
};

export default ProposalPage;